import type { DashboardPayload } from '../types/payload'
import { calculateGlobalRupturePctExcludingDigital } from './rupturaMetrics'

const URL_FUNCTION_PATTERN = /url\((['"]?)([^'")]+)\1\)/g

async function toDataUri(url: string): Promise<string> {
  if (url.startsWith('data:')) {
    return url
  }

  const response = await fetch(url)
  const blob = await response.blob()

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function inlineExternalStylesheet(href: string): Promise<string> {
  const cssText = await (await fetch(href)).text()
  const matches = Array.from(cssText.matchAll(URL_FUNCTION_PATTERN))
  let result = cssText

  for (const match of matches) {
    const assetUrl = new URL(match[2], href).toString()

    try {
      const dataUri = await toDataUri(assetUrl)
      result = result.split(match[0]).join(`url("${dataUri}")`)
    } catch {
      // Asset unreachable (offline, CORS, a Google Fonts file that changed,
      // ...) — a raw https:// url() left in the export is exactly what
      // hangs iOS WebKit on a file:// document, so drop the reference
      // instead of keeping it. The rule just resolves to no source (e.g. a
      // skipped @font-face src) rather than attempting a network fetch.
      result = result.split(match[0]).join('url(data:,)')
    }
  }

  return result
}

/**
 * Final safety net over collectStyles()'s own per-asset handling: strips any
 * http(s):// reference that could still trigger a network fetch (an @import
 * whose whole stylesheet fetch failed, or any url() this pass didn't already
 * catch). The exported file must have zero network dependencies — a leftover
 * external reference here is a real bug, not a soft-fail, so this always
 * wins over keeping a "maybe it'll load" reference.
 */
function stripExternalUrls(cssText: string): string {
  let strippedCount = 0

  const withoutImports = cssText.replace(
    /@import\s+(?:url\()?(['"]?)(https?:\/\/[^'")]+)\1\)?\s*;?/gi,
    () => {
      strippedCount += 1
      return ''
    },
  )

  const sanitized = withoutImports.replace(
    /url\((['"]?)(https?:\/\/[^'")]+)\1\)/gi,
    () => {
      strippedCount += 1
      return 'url(data:,)'
    },
  )

  if (strippedCount > 0) {
    console.warn(
      `[exportToStandaloneHtml] Removidas ${strippedCount} referência(s) externa(s) de rede do CSS exportado (garantia de zero dependência de rede).`,
    )
  }

  return sanitized
}

async function collectStyles(): Promise<string> {
  const chunks: string[] = []

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      chunks.push(Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n'))
    } catch {
      if (sheet.href) {
        try {
          chunks.push(await inlineExternalStylesheet(sheet.href))
        } catch {
          // Whole stylesheet unreachable — skip it entirely rather than
          // falling back to a literal @import of the external URL.
        }
      }
    }
  }

  return stripExternalUrls(chunks.join('\n'))
}

async function getAppBundleSource(): Promise<string> {
  // The entry script is the last `type="module"` tag with a `src` — in a
  // production build it's also the only one, but this stays correct even if
  // preload/vendor script tags are ever added ahead of it.
  const scriptEls = document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
  const scriptEl = scriptEls[scriptEls.length - 1]

  if (!scriptEl) {
    throw new Error('Não foi possível localizar o script da aplicação para embutir na exportação.')
  }

  const response = await fetch(scriptEl.src)

  if (!response.ok) {
    throw new Error(`Falha ao obter o bundle da aplicação (HTTP ${response.status}).`)
  }

  const text = await response.text()
  // The source map file isn't bundled into the export, so drop the
  // reference to avoid a dangling 404 lookup when DevTools is open.
  return text.replace(/\/\/# sourceMappingURL=[^\n]*\n?/, '')
}

function escapeScriptClose(text: string): string {
  return text.replace(/<\/script/gi, '<\\/script')
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function buildStaticPreviewNotice(): string {
  return `
<div class="staticPreviewNotice">
  <strong>Prévia offline</strong> — navegue pela barra lateral e pelos filtros normalmente. Para exportar um
  gráfico em PNG ou trocar de edição, abra o arquivo no navegador do seu aparelho.
</div>`
}

/**
 * Converts a live <canvas> (Chart.js) into a static <img> at the same
 * rendered size, using the pixel data already on screen — no re-render, no
 * network. Chart.js is the only canvas-based chart type in this app; the
 * others are hand-rolled SVG or plain DOM/CSS bars, both of which survive
 * cloneNode() unchanged since they're just markup.
 *
 * The capture always runs at whatever viewport generated the export (in
 * practice, desktop) — a literal `width: <that>px` baked into the <img>
 * would stay that size forever, overflowing a narrow phone screen. `width:
 * 100%` + `max-width` (the desktop capture width, as an upper bound so it
 * never upscales past its native resolution) + `height: auto` keeps it
 * responsive instead, matching how the live canvas itself resizes.
 */
function cloneCanvasAsImage(canvas: HTMLCanvasElement): HTMLImageElement {
  const img = document.createElement('img')
  img.src = canvas.toDataURL('image/png')
  img.alt = ''
  const computed = window.getComputedStyle(canvas)
  img.style.width = '100%'
  img.style.maxWidth = computed.width
  img.style.height = 'auto'
  img.style.display = computed.display || 'block'
  if (canvas.className) {
    img.className = canvas.className
  }
  return img
}

/**
 * Strips controls that can't be made to work without JavaScript: the top
 * "Exportar" menu (re-exporting the whole page from within itself makes no
 * sense) and the multi-edition <select> (switching edition needs a
 * different embedded payload this file doesn't have). Per-chart "Exportar
 * gráfico em PNG" buttons are deliberately left in place, inert — baking a
 * PNG per button ahead of time means one per toggle combination (dozens per
 * section), which is exactly the mass-embedding that bloated a past export
 * from ~3MB to ~10MB. The static preview notice above already discloses
 * that PNG export needs JavaScript, same as the multi-edition picker.
 */
function stripNonFunctionalControls(root: HTMLElement): void {
  const selectors = ['[class*="exportMenu"]', 'select']

  for (const selector of selectors) {
    root.querySelectorAll(selector).forEach(el => el.remove())
  }
}

/**
 * Captures the currently-mounted dashboard exactly as rendered on screen —
 * full sidebar, every section, every table, every chart — as static HTML.
 * This is what makes the export "navigable" without JavaScript: the sidebar
 * already links to real `#section-id` anchors matching real `id` attributes
 * on each section (Dashboard.tsx), so the browser's native anchor-jump
 * handles navigation with zero script. Only in-page state toggles (segment
 * selector, density mode tabs) can't be replicated statically — those still
 * need the live React app, hence the notice above this content.
 */
async function cloneElementCapturingCanvases(liveEl: HTMLElement): Promise<string> {
  const clone = liveEl.cloneNode(true) as HTMLElement
  const liveCanvases = Array.from(liveEl.querySelectorAll('canvas'))
  const clonedCanvases = Array.from(clone.querySelectorAll('canvas'))

  liveCanvases.forEach((liveCanvas, index) => {
    const clonedCanvas = clonedCanvases[index]

    if (!clonedCanvas) {
      return
    }

    try {
      clonedCanvas.replaceWith(cloneCanvasAsImage(liveCanvas as HTMLCanvasElement))
    } catch {
      // leave the blank canvas in place rather than fail the whole capture
    }
  })

  return clone.outerHTML
}

/**
 * Chart.js (and React) don't finish re-rendering the instant a click
 * handler returns — state updates commit async, and Chart.js animates data
 * transitions by default. Two rAFs get past React's commit/paint cycle;
 * the extra delay gives Chart.js's default ~750ms transition time to
 * settle before toDataURL() reads the canvas.
 */
function waitForRepaint(extraDelayMs = 380): Promise<void> {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, extraDelayMs)
      })
    })
  })
}

interface ToggleDimension {
  groupName: string
  buttons: HTMLElement[]
}

interface ContentRegion {
  key: string
  dependsOn: string[]
  selector: string
}

/**
 * Drives the LIVE app through every combination of the given toggle
 * dimensions (real clicks on the real buttons), capturing each content
 * region's rendered HTML after each combination settles. A region only
 * gets re-captured when a combo of ITS OWN dependsOn dimensions is new
 * (e.g. a region that only depends on "segment" is captured 3 times, not
 * 9, even when nested inside a 3×3 combination loop).
 */
async function captureToggleStates(
  liveSectionRoot: HTMLElement,
  dimensions: ToggleDimension[],
  regions: ContentRegion[],
): Promise<Map<string, string>> {
  const captured = new Map<string, string>()

  function regionKey(region: ContentRegion, chosen: number[]): string {
    return region.key + '|' + region.dependsOn
      .map(groupName => `${groupName}=${chosen[dimensions.findIndex(d => d.groupName === groupName)]}`)
      .join('&')
  }

  async function captureAtCurrentState(chosen: number[]): Promise<void> {
    for (const region of regions) {
      const key = regionKey(region, chosen)

      if (captured.has(key)) {
        continue
      }

      const el = liveSectionRoot.querySelector<HTMLElement>(region.selector)

      if (el) {
        captured.set(key, await cloneElementCapturingCanvases(el))
      }
    }
  }

  async function recurse(dimIndex: number, chosen: number[]): Promise<void> {
    if (dimIndex === dimensions.length) {
      await captureAtCurrentState(chosen)
      return
    }

    const dim = dimensions[dimIndex]

    for (let i = 0; i < dim.buttons.length; i++) {
      const btn = dim.buttons[i] as HTMLButtonElement

      if (btn.disabled) {
        continue
      }

      btn.click()
      await waitForRepaint()
      chosen[dimIndex] = i
      await recurse(dimIndex + 1, chosen)
    }
  }

  await recurse(0, new Array(dimensions.length).fill(0))
  return captured
}

function getEnabledButtons(buttons: HTMLElement[]): HTMLElement[] {
  return buttons.filter(b => !(b as HTMLButtonElement).disabled)
}

function readRestingLabelsAndClasses(buttons: HTMLElement[], activeClassNeedle: string) {
  return {
    labels: buttons.map(b => b.textContent?.trim() ?? ''),
    classes: buttons.map(b =>
      b.className.split(/\s+/).filter(token => !token.includes(activeClassNeedle)).join(' '),
    ),
  }
}

interface ToggleGroupSpec {
  groupName: string
  labels: string[]
  classes: string[]
}

function htmlStringToNodes(html: string): Node[] {
  const wrapper = document.createElement('div')
  wrapper.innerHTML = html
  return Array.from(wrapper.childNodes)
}

/**
 * Flat, unwrapped <input> list — deliberately NOT nested inside any row
 * container, so every radio is a true sibling of every later panel (and of
 * every label), which is what makes the cheap `~` sibling combinator work
 * below for both panel visibility and active-label highlighting.
 */
function buildToggleRadiosHtml(group: ToggleGroupSpec): string {
  return group.labels.map((_, i) =>
    `<input type="radio" name="${group.groupName}" id="${group.groupName}-opt${i}" class="exportToggleRadio"${i === 0 ? ' checked' : ''}>`,
  ).join('')
}

function buildToggleLabelsHtml(group: ToggleGroupSpec): string {
  return group.labels.map((label, i) =>
    `<label for="${group.groupName}-opt${i}" class="${escapeHtml(group.classes[i])}">${escapeHtml(label)}</label>`,
  ).join('')
}

function activeStyleDeclarationsFor(restingClass: string): string {
  if (restingClass.includes('segmentPill')) {
    return 'background: var(--mi7-blue); color: var(--white);'
  }

  if (restingClass.includes('tabChip')) {
    return 'background: rgba(12, 83, 154, 0.14); border-color: rgba(12, 83, 154, 0.22);'
  }

  return ''
}

function buildToggleActiveCss(group: ToggleGroupSpec): string[] {
  return group.labels
    .map((_, i) => {
      const decl = activeStyleDeclarationsFor(group.classes[i])
      return decl
        ? `#${group.groupName}-opt${i}:checked ~ label[for="${group.groupName}-opt${i}"] { ${decl} }`
        : ''
    })
    .filter(Boolean)
}

interface PanelCombo {
  comboSuffix: string
  radioSelectors: string[]
}

/**
 * Plain sibling-combinator (~) chains — not :has(). An earlier version used
 * :has(), which forces the browser to re-scan potentially large descendant
 * subtrees on every radio-state change: measured 8-30 SECONDS of jank on
 * this page's multi-megabyte captured content. `~` only walks preceding
 * siblings — the same lightweight technique CSS-only tab/accordion hacks
 * have used for over a decade — which is why every radio, label, and panel
 * is emitted as a flat, unwrapped sibling (see buildInteractiveZoneHtml).
 */
function buildPanelsAndCss(
  captured: Map<string, string>,
  regionKeyPrefix: string,
  combos: PanelCombo[],
): { panelsHtml: string; cssRules: string[] } {
  let panelsHtml = ''
  const cssRules: string[] = []

  for (const combo of combos) {
    const key = `${regionKeyPrefix}|${combo.comboSuffix}`
    const html = captured.get(key)

    if (html === undefined) {
      continue
    }

    const panelId = `${regionKeyPrefix}-${combo.comboSuffix.replace(/[^a-z0-9]/gi, '-')}`
    panelsHtml += `<div class="exportTogglePanel" data-panel-id="${panelId}">${html}</div>`

    const chain = combo.radioSelectors.map(id => `#${id}:checked`).join(' ~ ')
    cssRules.push(`${chain} ~ [data-panel-id="${panelId}"] { display: block; }`)
  }

  return { panelsHtml, cssRules }
}

/**
 * Combines N toggle groups' radios + labels (each group's labels followed
 * by a row-break spacer, so they visually wrap onto separate lines inside
 * a flex container) with the already-built panel markup, all as flat,
 * unwrapped children of one zone div.
 */
function toggleLabelsBlock(group: ToggleGroupSpec): string {
  return buildToggleLabelsHtml(group) + '<div class="exportToggleRowBreak"></div>'
}

/**
 * `visualBlocks` is rendered in the exact order given — this is what lets
 * a picker sit directly above the content it controls (e.g. Índice de
 * Cesta's mode tabs need to land right above the density charts, not
 * bunched with the segment picker at the top). All of a section's radios
 * are still collected first regardless of visual order: they're invisible,
 * so their own position doesn't matter, but every one of them must precede
 * every panel in document order for the `~` combinator to reach it.
 */
function buildInteractiveZoneHtml(groups: ToggleGroupSpec[], visualBlocks: string[]): string {
  const radios = groups.map(buildToggleRadiosHtml).join('')
  return `<div class="exportInteractiveZone">${radios}${visualBlocks.join('')}</div>`
}

/**
 * Índice de Cesta has two interdependent toggles: segment (Fralda Infantil
 * / Lenço Umedecido / Sabonete Líquido) and density mode (%, Marcas, SKUs).
 * The weekly matrix only depends on segment (3 captures); the density
 * panel depends on both (up to 9 captures).
 */
async function rebuildIndiceCestaInteractive(clone: HTMLElement): Promise<void> {
  const liveSection = document.getElementById('indice-cesta')
  const cloneSection = clone.querySelector('#indice-cesta') as HTMLElement | null

  if (!liveSection || !cloneSection) {
    return
  }

  const segmentButtons = getEnabledButtons(Array.from(
    liveSection.querySelectorAll<HTMLElement>('[role="tablist"][aria-label="Segmentos do índice cesta"] button'),
  ))
  const modeButtons = getEnabledButtons(
    Array.from(liveSection.querySelectorAll<HTMLElement>('[class*="tabRow"] button')),
  )

  if (segmentButtons.length === 0 || modeButtons.length === 0) {
    return
  }

  const seg = readRestingLabelsAndClasses(segmentButtons, 'segmentPillActive')
  const mode = readRestingLabelsAndClasses(modeButtons, 'tabChipActive')
  const segGroup: ToggleGroupSpec = { groupName: 'cesta-seg', labels: seg.labels, classes: seg.classes }
  const modeGroup: ToggleGroupSpec = { groupName: 'cesta-mode', labels: mode.labels, classes: mode.classes }

  const dimensions: ToggleDimension[] = [
    { groupName: 'cesta-seg', buttons: segmentButtons },
    { groupName: 'cesta-mode', buttons: modeButtons },
  ]
  const regions: ContentRegion[] = [
    { key: 'split', dependsOn: ['cesta-seg'], selector: '[class*="editorialSplit"]' },
    { key: 'density', dependsOn: ['cesta-seg', 'cesta-mode'], selector: '[class*="densityMatrix"]' },
  ]

  const captured = await captureToggleStates(liveSection, dimensions, regions)

  segmentButtons[0]?.click()
  modeButtons[0]?.click()
  await waitForRepaint(50)

  const originalSegmentSelector = cloneSection.querySelector(
    '[role="tablist"][aria-label="Segmentos do índice cesta"]',
  )
  const originalEditorialSplit = cloneSection.querySelector('[class*="editorialSplit"]')
  const originalTabRow = cloneSection.querySelector('[class*="tabRow"]')
  const originalDensityMatrix = cloneSection.querySelector('[class*="densityMatrix"]')

  if (!originalSegmentSelector || !originalEditorialSplit || !originalTabRow || !originalDensityMatrix) {
    return
  }

  const splitCombos: PanelCombo[] = seg.labels.map((_, s) => ({
    comboSuffix: `cesta-seg=${s}`,
    radioSelectors: [`cesta-seg-opt${s}`],
  }))
  const { panelsHtml: splitPanelsHtml, cssRules: splitCss } = buildPanelsAndCss(captured, 'split', splitCombos)

  const densityCombos: PanelCombo[] = []
  seg.labels.forEach((_, s) => {
    mode.labels.forEach((_, m) => {
      densityCombos.push({
        comboSuffix: `cesta-seg=${s}&cesta-mode=${m}`,
        radioSelectors: [`cesta-seg-opt${s}`, `cesta-mode-opt${m}`],
      })
    })
  })
  const { panelsHtml: densityPanelsHtml, cssRules: densityCss } = buildPanelsAndCss(captured, 'density', densityCombos)

  // Visual order matches the original page: segment picker, then the
  // matrix it controls, THEN the mode picker, then the density charts it
  // controls — not both pickers bunched together above everything.
  const zoneHtml = buildInteractiveZoneHtml(
    [segGroup, modeGroup],
    [toggleLabelsBlock(segGroup), splitPanelsHtml, toggleLabelsBlock(modeGroup), densityPanelsHtml],
  )
  originalEditorialSplit.replaceWith(...htmlStringToNodes(zoneHtml))
  originalDensityMatrix.remove()
  originalSegmentSelector.remove()
  originalTabRow.remove()

  const styleEl = document.createElement('style')
  styleEl.textContent = [
    ...splitCss,
    ...densityCss,
    ...buildToggleActiveCss(segGroup),
    ...buildToggleActiveCss(modeGroup),
  ].join('\n')
  cloneSection.appendChild(styleEl)
}

/**
 * Variação de Preços: granularity (Anual/Semanal) × department (10+
 * categories) — both drive the same SVG line chart + KPI card region.
 */
async function rebuildVariacaoPrecosInteractive(clone: HTMLElement): Promise<void> {
  const liveSection = document.getElementById('variacao-precos')
  const cloneSection = clone.querySelector('#variacao-precos') as HTMLElement | null

  if (!liveSection || !cloneSection) {
    return
  }

  const viewButtons = getEnabledButtons(Array.from(
    liveSection.querySelectorAll<HTMLElement>('[role="tablist"][aria-label="Granularidade da série"] button'),
  ))
  const categoryButtons = getEnabledButtons(Array.from(
    liveSection.querySelectorAll<HTMLElement>('[role="tablist"][aria-label="Departamentos"] button'),
  ))

  if (viewButtons.length === 0 || categoryButtons.length === 0) {
    return
  }

  const view = readRestingLabelsAndClasses(viewButtons, 'tabChipActive')
  const cat = readRestingLabelsAndClasses(categoryButtons, 'tabChipActive')
  const viewGroup: ToggleGroupSpec = { groupName: 'vp-view', labels: view.labels, classes: view.classes }
  const catGroup: ToggleGroupSpec = { groupName: 'vp-cat', labels: cat.labels, classes: cat.classes }

  const dimensions: ToggleDimension[] = [
    { groupName: 'vp-view', buttons: viewButtons },
    { groupName: 'vp-cat', buttons: categoryButtons },
  ]
  const regions: ContentRegion[] = [
    { key: 'chart', dependsOn: ['vp-view', 'vp-cat'], selector: '[class*="chartCard"]' },
  ]

  const captured = await captureToggleStates(liveSection, dimensions, regions)

  viewButtons[0]?.click()
  await waitForRepaint(50)
  categoryButtons[0]?.click()
  await waitForRepaint(50)

  const originalViewTabs = cloneSection.querySelector('[role="tablist"][aria-label="Granularidade da série"]')
  const originalCategoryTabs = cloneSection.querySelector('[role="tablist"][aria-label="Departamentos"]')
  const originalChartCard = cloneSection.querySelector('[class*="chartCard"]')

  if (!originalViewTabs || !originalCategoryTabs || !originalChartCard) {
    return
  }

  const combos: PanelCombo[] = []
  view.labels.forEach((_, v) => {
    cat.labels.forEach((_, c) => {
      combos.push({
        comboSuffix: `vp-view=${v}&vp-cat=${c}`,
        radioSelectors: [`vp-view-opt${v}`, `vp-cat-opt${c}`],
      })
    })
  })
  const { panelsHtml, cssRules } = buildPanelsAndCss(captured, 'chart', combos)

  const zoneHtml = buildInteractiveZoneHtml(
    [viewGroup, catGroup],
    [toggleLabelsBlock(viewGroup), toggleLabelsBlock(catGroup), panelsHtml],
  )
  originalChartCard.replaceWith(...htmlStringToNodes(zoneHtml))
  originalViewTabs.remove()
  originalCategoryTabs.remove()

  const styleEl = document.createElement('style')
  styleEl.textContent = [
    ...cssRules,
    ...buildToggleActiveCss(viewGroup),
    ...buildToggleActiveCss(catGroup),
  ].join('\n')
  cloneSection.appendChild(styleEl)
}

/** Ruptura Digital: a single dimension (channel). */
async function rebuildRupturaDigitalInteractive(clone: HTMLElement): Promise<void> {
  const liveSection = document.getElementById('ruptura-digital')
  const cloneSection = clone.querySelector('#ruptura-digital') as HTMLElement | null

  if (!liveSection || !cloneSection) {
    return
  }

  const channelButtons = getEnabledButtons(Array.from(
    liveSection.querySelectorAll<HTMLElement>('[role="tablist"][aria-label="Canais de ruptura"] button'),
  ))

  if (channelButtons.length === 0) {
    return
  }

  const channel = readRestingLabelsAndClasses(channelButtons, 'tabChipActive')
  const channelGroup: ToggleGroupSpec = { groupName: 'ruptura-channel', labels: channel.labels, classes: channel.classes }

  const dimensions: ToggleDimension[] = [{ groupName: 'ruptura-channel', buttons: channelButtons }]
  const regions: ContentRegion[] = [
    { key: 'grid', dependsOn: ['ruptura-channel'], selector: '[class*="ruptureCompareGrid"]' },
  ]

  const captured = await captureToggleStates(liveSection, dimensions, regions)

  channelButtons[0]?.click()
  await waitForRepaint(50)

  const originalTabRow = cloneSection.querySelector('[role="tablist"][aria-label="Canais de ruptura"]')
  const originalGrid = cloneSection.querySelector('[class*="ruptureCompareGrid"]')

  if (!originalTabRow || !originalGrid) {
    return
  }

  const combos: PanelCombo[] = channel.labels.map((_, i) => ({
    comboSuffix: `ruptura-channel=${i}`,
    radioSelectors: [`ruptura-channel-opt${i}`],
  }))
  const { panelsHtml, cssRules } = buildPanelsAndCss(captured, 'grid', combos)

  const zoneHtml = buildInteractiveZoneHtml([channelGroup], [toggleLabelsBlock(channelGroup), panelsHtml])
  originalGrid.replaceWith(...htmlStringToNodes(zoneHtml))
  originalTabRow.remove()

  const styleEl = document.createElement('style')
  styleEl.textContent = [...cssRules, ...buildToggleActiveCss(channelGroup)].join('\n')
  cloneSection.appendChild(styleEl)
}

async function captureLiveDashboardSnapshot(): Promise<string | null> {
  const liveRoot = document.getElementById('root')

  if (!liveRoot || liveRoot.children.length === 0) {
    return null
  }

  try {
    const clone = liveRoot.cloneNode(true) as HTMLElement
    const liveCanvases = Array.from(liveRoot.querySelectorAll('canvas'))
    const clonedCanvases = Array.from(clone.querySelectorAll('canvas'))

    liveCanvases.forEach((liveCanvas, index) => {
      const clonedCanvas = clonedCanvases[index]

      if (!clonedCanvas) {
        return
      }

      try {
        clonedCanvas.replaceWith(cloneCanvasAsImage(liveCanvas))
      } catch {
        // A tainted or zero-size canvas can't be read via toDataURL — leave
        // the (blank) canvas element in place rather than fail the whole
        // snapshot over a single chart.
      }
    })

    // Rebuild every in-page toggle (segment/mode, category/granularity,
    // channel) as a pure-CSS radio group (sibling-combinator driven) so
    // they actually work under iOS Quick Look, which renders this file
    // with JavaScript disabled. Each rebuild drives the LIVE app through
    // every option via real clicks, capturing real rendered content
    // (including redrawn Chart.js canvases), then restores the live app's
    // original visible state before moving to the next section. Per-chart
    // PNG export buttons stay inert in the static snapshot — see
    // stripNonFunctionalControls for why.
    await rebuildIndiceCestaInteractive(clone)
    await rebuildVariacaoPrecosInteractive(clone)
    await rebuildRupturaDigitalInteractive(clone)

    stripNonFunctionalControls(clone)

    return clone.innerHTML
  } catch {
    return null
  }
}

/**
 * Emergency fallback if captureLiveDashboardSnapshot() ever returns null
 * (e.g. called outside a mounted dashboard). Not the primary path anymore —
 * kept only so a capture failure degrades to a useful branded summary
 * instead of a blank #root.
 */
function buildStaticFallbackHtml(payload: DashboardPayload): string {
  const { meta, sections, executive_summary: summary } = payload
  const rupturaPct = calculateGlobalRupturePctExcludingDigital(sections.ruptura_digital.records)

  const bullets = summary.bullets
    .slice(0, 3)
    .map(bullet => `<li>${escapeHtml(bullet)}</li>`)
    .join('')

  return `
<div class="staticFallback">
  <p class="staticFallbackBrand">ABMAPRO &times; Grupo Mi7</p>
  <h1 class="staticFallbackTitle">Portal INMP</h1>
  <p class="staticFallbackEdition">${escapeHtml(meta.edition_label)}</p>
  <div class="staticFallbackKpis">
    <div class="staticFallbackKpi">
      <span>SKUs únicos</span>
      <strong>${sections.quantidade_mp.totals.distinct_produtos.toLocaleString('pt-BR')}</strong>
    </div>
    <div class="staticFallbackKpi">
      <span>Capturas brutas</span>
      <strong>${sections.quantidade_mp.totals.rows.toLocaleString('pt-BR')}</strong>
    </div>
    <div class="staticFallbackKpi">
      <span>Ruptura global (excl. Digital)</span>
      <strong>${(rupturaPct * 100).toFixed(1)}%</strong>
    </div>
  </div>
  <h2 class="staticFallbackHeadline">${escapeHtml(summary.headline)}</h2>
  <p class="staticFallbackSummary">${escapeHtml(summary.summary)}</p>
  ${bullets ? `<ul class="staticFallbackBullets">${bullets}</ul>` : ''}
  <p class="staticFallbackNotice">Esta é uma prévia estática. Para o painel completo — com abas, gráficos e tabelas interativas — toque em <strong>"⋯"</strong> ou <strong>"Compartilhar"</strong> e escolha <strong>"Abrir no Safari"</strong>: a pré-visualização rápida do iOS não executa conteúdo interativo.</p>
</div>`
}

export async function buildStandaloneHtml(payload: DashboardPayload): Promise<string> {
  if (import.meta.env.DEV) {
    // `vite dev` serves unbundled ES modules resolved through Vite's own dev
    // server (bare specifiers, /@fs/ paths, /@vite/client, ...). None of that
    // can be embedded as a self-contained script — only a production build
    // (`vite build` + `vite preview`, or the deployed site) has a single
    // bundled entry file that this function can safely inline.
    throw new Error(
      'A exportação em HTML só funciona a partir de uma build de produção (npm run build / preview), não no servidor de desenvolvimento (npm run dev).',
    )
  }

  // Captured first, before the other async work below: this drives the
  // live app through every toggle combination (real clicks, real redraws),
  // so it takes real time — the export button shows "Gerando…" for the
  // whole call, which covers this.
  const dashboardSnapshot = await captureLiveDashboardSnapshot()
  const rootContent = dashboardSnapshot !== null
    ? buildStaticPreviewNotice() + dashboardSnapshot
    : buildStaticFallbackHtml(payload)

  const [css, appBundleSource] = await Promise.all([collectStyles(), getAppBundleSource()])
  const title = `Portal INMP — ${payload.meta.edition_label}`

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
<meta name="format-detection" content="telephone=no" />
<title>${title}</title>
<style>
/* Percentage-based fallback ahead of the app's own CSS (which sizes
   body/#root with 100dvh) — engines that don't support dvh simply skip
   that later declaration, leaving this in effect, instead of collapsing
   #root to zero height on a local file:// document. */
html, body, #root {
  min-height: 100%;
  width: 100%;
  background-color: #f8fafc;
  margin: 0;
  padding: 0;
}
/* The live site loads Cormorant Garamond/Inter from Google Fonts —
   collectStyles() strips any of that CSS it can't fully inline as data
   URIs (see stripExternalUrls), so force native system fonts here as a
   guaranteed, zero-network baseline regardless of how that inlining went. */
body, button, input, select, textarea {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
}
</style>
<style>
${css}
</style>
<style>
/* Mobile touch-scroll support for the weekly index matrix and bar-table
   containers — the exported file has no responsive breakpoint tuning of
   its own, so these stay scrollable instead of clipping/squishing on
   narrow iOS/Android viewports. */
[class*="weeklyIndexTable"],
[class*="weeklyIndexMatrix"],
[class*="barTable"],
[class*="tableWrapper"],
[class*="matrixContainer"],
.table-container {
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
}
</style>
<style>
/* Pure-CSS toggle mechanism backing the rebuilt Índice de Cesta / Variação
   de Preços / Ruptura Digital sections, so their filters keep working with
   JavaScript fully disabled (iOS Quick Look). Uses the plain sibling
   combinator (~), not :has() — :has() forced the browser to re-scan large
   descendant subtrees on every tap, measured at 8-30 seconds of jank on
   this page's content. ~ only walks preceding siblings, so every radio,
   label, and panel is emitted flat/unwrapped (see buildInteractiveZoneHtml
   in the export code) rather than nested in a row container. */
.exportInteractiveZone {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
}
.exportToggleRadio {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
/* Forces a flex line-break after each toggle group's labels, so segment
   pills and mode tabs still land on visually separate rows despite no
   longer being nested in their own row <div> (which :checked ~ label can't
   see through — general sibling combinators don't cross into children). */
.exportToggleRowBreak {
  flex-basis: 100%;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
}
.exportTogglePanel {
  display: none;
  flex-basis: 100%;
  width: 100%;
}
/* Active-pill styling, mirroring .segmentPillActive / .tabChipActive from
   the app's own CSS — duplicated here as literal declarations (not a
   className toggle, which plain CSS can't do) so a checked radio's label
   visually matches what the live button looked like when selected. Exact
   rules are generated per toggle group (see buildToggleActiveCss) and
   appended inside each rebuilt section. */
</style>
<style>
/* Notice banner shown above the captured dashboard snapshot — kept in its
   own dedicated block (not the app's own CSS) so it renders correctly even
   if the collected stylesheet below fails to apply for any reason. */
.staticPreviewNotice {
  display: none;
  max-width: 720px;
  margin: 16px auto 0;
  padding: 12px 16px;
  border-radius: 12px;
  background: #fffbea;
  border: 1px solid rgba(180, 140, 0, 0.25);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  font-size: 0.82rem;
  line-height: 1.5;
  color: #4a3c00;
}
@media (max-width: 900px) {
  .staticPreviewNotice {
    display: block;
  }
}

/* Emergency fallback card — only used if captureLiveDashboardSnapshot()
   fails outright. Not the primary path; see buildStaticFallbackHtml. */
.staticFallback {
  max-width: 640px;
  margin: 10vh auto;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(21, 24, 40, 0.08), 0 12px 32px rgba(21, 24, 40, 0.08);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  color: #151828;
}
.staticFallbackBrand {
  margin: 0 0 4px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5f667a;
}
.staticFallbackTitle {
  margin: 0 0 2px;
  font-size: 1.6rem;
  font-weight: 700;
}
.staticFallbackEdition {
  margin: 0 0 20px;
  font-size: 0.95rem;
  color: #5f667a;
}
.staticFallbackKpis {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.staticFallbackKpi {
  flex: 1 1 140px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(21, 24, 40, 0.06);
}
.staticFallbackKpi span {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5f667a;
  margin-bottom: 4px;
}
.staticFallbackKpi strong {
  font-size: 1.25rem;
}
.staticFallbackHeadline {
  margin: 0 0 8px;
  font-size: 1.1rem;
  font-weight: 700;
}
.staticFallbackSummary {
  margin: 0 0 12px;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #33384a;
}
.staticFallbackBullets {
  margin: 0 0 20px;
  padding-left: 1.1rem;
  font-size: 0.88rem;
  line-height: 1.6;
  color: #33384a;
}
.staticFallbackNotice {
  margin: 0;
  padding-top: 16px;
  border-top: 1px solid rgba(21, 24, 40, 0.08);
  font-size: 0.8rem;
  line-height: 1.5;
  color: #8792a4;
}
</style>
</head>
<body>
<div id="root">${rootContent}</div>
<script>window.__INMP_EMBEDDED_PAYLOAD__ = ${escapeScriptClose(JSON.stringify(payload))};</script>
<script>
document.addEventListener('DOMContentLoaded', function () {
'use strict';
${escapeScriptClose(appBundleSource)}
});
</script>
</body>
</html>`
}

export async function downloadStandaloneHtml(payload: DashboardPayload): Promise<void> {
  const html = await buildStandaloneHtml(payload)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = `Portal_INMP_Edicao_${payload.meta.period.replace(/-/g, '_')}.html`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
